/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useCallback, useMemo, useRef } from "react";
import { DirectoryContext } from "./core/Directory.context";
import { useDirectory } from "./core/Directory.hook";
import type { DirectoryConfig, DirectoryContextValue } from "./core/Directory.types";
import { useChangeAnnouncement } from "./core/useChangeAnnouncement.hook";

export type DirectoryMoleculeProps<T> = DirectoryConfig<T> & {
	children: React.ReactNode;
};

// Visually-hidden but readable by screen readers. Standard "sr-only" pattern.
const srOnlyStyles = css({
	position: "absolute",
	width: "1px",
	height: "1px",
	padding: "0",
	margin: "-1px",
	overflow: "hidden",
	clip: "rect(0, 0, 0, 0)",
	whiteSpace: "nowrap",
	borderWidth: "0",
});

function defaultCountLabel(count: number): string {
	return count === 1 ? "1 result" : `${count} results`;
}

function defaultPageLabel(page: number, totalPages: number): string {
	return `Page ${page} of ${totalPages}`;
}

/**
 * State container for Directory. Calls `useDirectory(config)`, wires the result into
 * `DirectoryContext`, and renders its children. Kept separate from `Directory.section.tsx`
 * so tests / Storybook can mount it without the section container chrome.
 *
 * Renders two visually-hidden `aria-live="polite"` regions:
 *   - **Count** — announces filtered result count when it changes (filter / search).
 *   - **Page**  — announces "Page X of Y" when the current page changes (pagination).
 *
 * They're split so a pagination event (count unchanged) still announces, and a
 * filter event (count changed) doesn't re-announce the page context redundantly.
 * Both gate the first render — initial counts/pages aren't a "state change" for AT.
 */
export function DirectoryMolecule<T>({ children, ...config }: DirectoryMoleculeProps<T>): React.JSX.Element {
	const result = useDirectory(config);

	const contextValue = useMemo<DirectoryContextValue>(
		() => ({
			config: config as unknown as DirectoryConfig<unknown>,
			result: result,
		}),
		[config, result],
	);

	// Stable format ref — caller may supply a fresh function reference on
	// every render (consumers commonly inline label callbacks). Wrapping in a
	// useCallback that reads through a ref keeps the underlying useEffect
	// dep stable without losing the latest implementation.
	const countLabelFn = config.resultsCountAnnouncementLabel ?? defaultCountLabel;
	const countFormatRef = useRef(countLabelFn);
	countFormatRef.current = countLabelFn;
	const formatCount = useCallback((count: number) => countFormatRef.current(count), []);
	const countAnnouncement = useChangeAnnouncement(result.totalFilteredCount, formatCount);

	// Stable format ref so the underlying useEffect dep doesn't churn on
	// every render. The format closure captures the LATEST `totalPages` via
	// a ref so a filter that changes totalPages doesn't stale-read the value.
	const pageLabelFn = config.pageAnnouncementLabel ?? defaultPageLabel;
	const pageFormatStateRef = useRef({ totalPages: result.totalPages, pageLabelFn });
	pageFormatStateRef.current = { totalPages: result.totalPages, pageLabelFn };
	const formatPage = useCallback(
		(page: number) => pageFormatStateRef.current.pageLabelFn(page, pageFormatStateRef.current.totalPages),
		[],
	);
	const rawPageAnnouncement = useChangeAnnouncement(result.currentPage, formatPage);
	// Silence "Page 1 of 1" — when the filter narrows results to a single
	// page the pagination UI is hidden, so an announcement would be noise.
	const pageAnnouncement = result.totalPages > 1 ? rawPageAnnouncement : "";

	return (
		<DirectoryContext.Provider value={contextValue}>
			<VStack alignItems="stretch" gap="eds.dimension.32" width="full">
				<div role="status" aria-live="polite" aria-atomic="true" className={srOnlyStyles}>
					{countAnnouncement}
				</div>
				<div role="status" aria-live="polite" aria-atomic="true" className={srOnlyStyles}>
					{pageAnnouncement}
				</div>
				{children}
			</VStack>
		</DirectoryContext.Provider>
	);
}
